/**
 * Repeat v2 (Ram, 2026-10-23) — a Loop step read as a sentence, and written
 * back, pure. The editor is RepeatStepEditorV2; codegen is unchanged.
 *
 *   Repeat while [count] [<] [10]
 *   Repeat for [i] from [0] up to [n]   (stopping before n | including n)
 *              counting (up | down) by [1]
 *   Repeat for [i] from [0] while [ … ]     ← any other stop
 *
 * Storage is the old editor's: `loopType`, `conditions` / `rawExpression`,
 * `forConfig {counter, start, step}`. "Stopping before / including" is the
 * operator of the stop: `<` / `≤` counting up, `>` / `≥` counting down; down
 * is a negative step. The stop is kept as ONE guided row
 * `{resource: counter, operator, value}` when the value is a number or a name
 * codegen will not mistake for text — Python's `range()` reads that row
 * (pythonRangeArgs) — and as `rawExpression` otherwise (a calculation, or a
 * name such as an outer counter that is not a declared value, which a guided
 * row would quote into text: needsStringQuotes).
 *
 * A legacy For that has only the old `forConfig.end` and no condition reads as
 * a count with NO stop: the editor says "Say when it stops" and fills nothing
 * in (Ram's decision).
 */
import { needsStringQuotes } from './codeGenerator';
import { loopConditionC, writeCondition, readingEnv } from './conditionText';
import {
  createEnv, tokenize, parse, emit,
} from '@/components/visual-flow-builder/ExpressionKeyboard/engine';

const trimmed = (s) => String(s == null ? '' : s).trim();
const NAME = /^[A-Za-z_]\w*$/;
const NUMBER = /^-?\d+$/;

const OPS = {
  lessThan: { c: '<', down: false, including: false },
  lessOrEqual: { c: '<=', down: false, including: true },
  greaterThan: { c: '>', down: true, including: false },
  greaterOrEqual: { c: '>=', down: true, including: true },
};
const C_TO_OP = { '<': 'lessThan', '<=': 'lessOrEqual', '>': 'greaterThan', '>=': 'greaterOrEqual' };

/** The stop's operator: up → `<` / `<=`, down → `>` / `>=`. */
export function stopOperator(down, including) {
  if (down) return including ? 'greaterOrEqual' : 'greaterThan';
  return including ? 'lessOrEqual' : 'lessThan';
}
export const stopOperatorC = (down, including) => OPS[stopOperator(down, including)].c;

/** A step string → { down, by }: '-1' → down by 1, '2' → up by 2. */
export function readStep(step) {
  const s = trimmed(step) || '1';
  if (s.startsWith('-')) return { down: true, by: trimmed(s.slice(1)) || '1' };
  return { down: false, by: s.replace(/^\+/, '').trim() || '1' };
}
export const writeStep = (down, by) => {
  const b = trimmed(by) || '1';
  return down ? `-${b}` : b;
};

/** The stop of a For as `counter <op> value`, from a guided row or a raw condition — or null. */
function readStop(node, counter, resources, env) {
  const raw = trimmed(node.rawExpression);
  if (!raw) {
    const conds = (Array.isArray(node.conditions) ? node.conditions : [])
      .filter((c) => c && (trimmed(c.resource) || trimmed(c.resourceFullPath)));
    if (conds.length !== 1) return null;
    const c = conds[0];
    if (trimmed(c.resourceFullPath || c.resource) !== trimmed(counter)) return null;
    if (!OPS[c.operator]) return null;
    const value = trimmed(c.value);
    if (!value) return null;
    if (needsStringQuotes(value, resources)) return null; // codegen writes it as text: not a count
    return { operator: c.operator, value, guided: true };
  }
  try {
    const e2 = readingEnv(raw, env);
    const r = parse(tokenize(raw, e2), e2);
    const t = r && r.tree;
    if (!t || r.err || t.t !== 'bin' || !C_TO_OP[t.op]) return null;
    const l = t.l && t.l.t === 'group' ? t.l.x : t.l;
    if (!l || l.t !== 'var' || l.v !== trimmed(counter)) return null;
    return { operator: C_TO_OP[t.op], value: emit(t.r), guided: false };
  } catch (e) {
    return null;
  }
}

/**
 * The Loop step as the editor shows it.
 *   → { kind: 'while', c }
 *   → { kind: 'count', counter, start, down, by, stop }
 *        stop: { mode: 'upto', value, including, guided } | { mode: 'while', c } | { mode: 'none' }
 *   → { kind: 'other', loopType }   (forEach / do-while: not offered in C)
 */
export function readRepeat(node, resources = [], env = createEnv()) {
  const loopType = (node && node.loopType) || 'while';
  if (loopType === 'while') return { kind: 'while', c: loopConditionC(node, resources) };
  if (loopType !== 'for') return { kind: 'other', loopType };
  const fc = node.forConfig || {};
  const counter = trimmed(fc.counter) || 'i';
  const start = trimmed(fc.start) || '0';
  const { down, by } = readStep(fc.step);
  const base = { kind: 'count', counter, start, down, by };
  if (fc.useExpressionMode && trimmed(fc.init)) return { ...base, expressionMode: true, stop: { mode: 'while', c: trimmed(fc.conditionExpr) } };
  const c = loopConditionC(node, resources);
  if (!c) return { ...base, stop: { mode: 'none' } };
  const stop = readStop(node, counter, resources, env);
  if (stop && OPS[stop.operator].down === down) {
    return { ...base, stop: { mode: 'upto', value: stop.value, including: OPS[stop.operator].including, guided: stop.guided } };
  }
  return { ...base, stop: { mode: 'while', c } };
}

/** Can the stop value go in ONE guided row without codegen misreading it? */
export function stopFitsGuidedRow(value, resources = []) {
  const v = trimmed(value);
  if (!v) return false;
  if (NUMBER.test(v)) return true;
  return NAME.test(v) && !needsStringQuotes(v, resources);
}

/** The node with a While written: the condition as the keyboard writes it. */
export function writeWhile(node, c) {
  return { ...writeCondition(node, c), loopType: 'while' };
}

/**
 * The node with a count written. `stop` is { mode: 'upto', value, including } or
 * { mode: 'while', c }. Only what changed is written; `forConfig` keeps every
 * other field it had (an old `end` included — codegen does not read it).
 */
export function writeCount(node, {
  counter, start, down, by, stop,
}, resources = [], { stopChanged = true } = {}) {
  const fc = node.forConfig || {};
  // Untouched fields stay exactly as they were stored.
  const forConfig = { ...fc };
  if (trimmed(fc.counter) !== trimmed(counter) || !('counter' in fc)) forConfig.counter = trimmed(counter);
  if (trimmed(fc.start) !== trimmed(start) || !('start' in fc)) forConfig.start = trimmed(start);
  const was = readStep(fc.step);
  if (was.down !== !!down || was.by !== (trimmed(by) || '1') || !('step' in fc)) forConfig.step = writeStep(down, by);
  if (fc.useExpressionMode) forConfig.useExpressionMode = false;
  const next = { ...node, loopType: 'for', forConfig };
  if (!stopChanged && trimmed(fc.counter) === trimmed(counter) && !fc.useExpressionMode) return next;
  if (stop.mode === 'upto') {
    const value = trimmed(stop.value);
    const operator = stopOperator(down, stop.including);
    if (stopFitsGuidedRow(value, resources)) {
      return {
        ...next,
        rawExpression: '',
        rawExpressionAst: null,
        conditionMode: 'guided',
        conditionLogic: 'AND',
        conditions: [{ id: `cond_${Date.now()}`, number: 1, resource: trimmed(counter), operator, value }],
      };
    }
    return { ...writeCondition(next, `${trimmed(counter)} ${OPS[operator].c} ${value}`) };
  }
  return writeCondition(next, trimmed(stop.c));
}

// ---- the read-back ---------------------------------------------------------------------------------
/** A value's number when it has a known one: a number, or a constant / #define with a number. */
export function knownNumber(v, resources = []) {
  const s = trimmed(v);
  if (NUMBER.test(s)) return Number(s);
  if (!NAME.test(s)) return null;
  const r = (resources || []).find((x) => x && x.name === s);
  if (!r) return null;
  const rt = String(r.resourceType || r.type || '').toLowerCase();
  if (rt !== 'constant' && rt !== 'macro') return null;
  const iv = trimmed(r.initialValue);
  return NUMBER.test(iv) ? Number(iv) : null;
}

const minus1 = (v, down) => {
  if (NUMBER.test(v)) return String(Number(v) + (down ? 1 : -1));
  if (NAME.test(v)) return `${v} ${down ? '+' : '−'} 1`;
  return null;
};

/**
 * What the count does, in words. Exact ONLY when start, stop and step all have
 * known values ("runs 10 times: i = 0 … 9"); otherwise it says the pattern in
 * words ("i takes 0, 1, 2, … up to n − 1") — never invented sample values.
 */
export function countReadBack({
  counter, start, down, by, stop,
}, resources = []) {
  const i = trimmed(counter) || 'i';
  const s0 = trimmed(start);
  const k0 = trimmed(by) || '1';
  if (!stop || stop.mode !== 'upto' || !trimmed(stop.value)) return null;
  const v = trimmed(stop.value);
  const s = knownNumber(s0, resources);
  const e = knownNumber(v, resources);
  const k = knownNumber(k0, resources);
  if (s !== null && e !== null && k !== null && k > 0) {
    let n;
    if (!down) n = stop.including ? Math.floor((e - s) / k) + 1 : Math.ceil((e - s) / k);
    else n = stop.including ? Math.floor((s - e) / k) + 1 : Math.ceil((s - e) / k);
    n = Math.max(0, n);
    if (n === 0) return { exact: true, count: 0, text: `never runs: ${i} starts at ${s0}, already past where it stops` };
    const vals = [];
    for (let j = 0; j < Math.min(n, 4); j += 1) vals.push(s + (down ? -1 : 1) * j * k);
    const last = s + (down ? -1 : 1) * (n - 1) * k;
    let list;
    if (n <= 3) list = vals.slice(0, n).join(', ');
    else if (k === 1) list = `${s} … ${last}`;
    else list = `${vals.slice(0, 3).join(', ')}, … ${last}`;
    return { exact: true, count: n, text: `runs ${n === 1 ? 'once' : `${n} times`}: ${i} = ${list}` };
  }
  // The pattern, in words.
  const step = k !== null ? k : null;
  const sign = down ? '−' : '+';
  let seq;
  if (s !== null && step !== null) {
    seq = [0, 1, 2].map((j) => s + (down ? -1 : 1) * j * step).join(', ');
  } else {
    const kk = step !== null ? String(step) : k0;
    seq = `${s0}, ${s0} ${sign} ${kk}, ${s0} ${sign} ${kk === '1' ? '2' : `2 × ${kk}`}`;
  }
  const dir = down ? 'down to' : 'up to';
  let end;
  if (stop.including) end = `${dir} ${v}`;
  else {
    const m = minus1(v, down);
    end = m ? `${dir} ${m}` : `${dir} just ${down ? 'after' : 'before'} ${v}`;
  }
  return { exact: false, count: null, text: `${i} takes ${seq}, … ${end}` };
}

/** The words for "stopping before n" / "including n". */
export const stopChoiceWords = (value, including) => (including ? `including ${trimmed(value) || 'it'}` : `stopping before ${trimmed(value) || 'it'}`);
